// The person's own voice signature (BUILD 2 C2-11, WP2-4 step 7), mounted on
// the Preferences page: unavailable, company off, "I agree" consent, recording,
// processing, enrolled (sources, delete) and stale-model states. Only the
// person enrols themselves; withdrawing deletes the voiceprint.
//
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { useNamespaceCopy } from '@/i18n/ns';
import voiceSignatureCopy from '@/i18n/ns/voice-signature';
import meetingsVoiceCopy from '@/i18n/ns/meetings-voice';
import { voiceApi, type VoiceMe } from './voice-api';
import { EnrolmentRecorder } from '@/voice/enrolment-recorder';
import { signatureError } from '@/voice/signature-error';
import { useCompany } from './context';
import { useWorkspace } from '@/workspace/context';

export function VoiceSignatureSection({ suggestionCount }: { suggestionCount?: number } = {}) {
  const copy = useNamespaceCopy(voiceSignatureCopy);
  const locale = useAppCatalogLocale();
  const meetingCopy = useNamespaceCopy(meetingsVoiceCopy);
  const [state, setState] = useState<VoiceMe | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);
  const busyToken = useRef(0);
  const request = useRef(0);
  const ws = useWorkspace();
  const company = useCompany();
  const scopeKey = `${company.data?.company?.id ?? ''}:${ws.user?.id ?? ''}`;
  const load = async (key: string, token: number) => {
    try {
      const next = await voiceApi.me();
      if (token === request.current && key === scopeKey) setState(next);
    } catch (e) {
      if (token === request.current && key === scopeKey) setError(signatureError(e, copy));
    }
  };
  useEffect(() => {
    const token = ++request.current;
    setState(null);
    setError(null);
    setChecked(false);
    busyRef.current = false;
    ++busyToken.current;
    setBusy(false);
    if (scopeKey !== ':') void load(scopeKey, token);
    return () => {
      request.current += 1;
    };
  }, [scopeKey]);
  const reload = () => void load(scopeKey, request.current);
  if (!state)
    return error ? (
      <section className="company-form">
        <p role="alert">{error}</p>
      </section>
    ) : (
      <p role="status">{copy.loading}</p>
    );
  if (!state.available && !state.voiceprint)
    return (
      <section className="company-form">
        <h2>{copy.title}</h2>
        <p>{copy.unavailable}</p>
      </section>
    );
  if (!state.org.signaturesEnabled && !state.voiceprint)
    return (
      <section className="company-form">
        <h2>{copy.title}</h2>
        <p>{copy.orgOff}</p>
      </section>
    );
  const deleteButton = (
    <button
      className="company-secondary"
      disabled={busy}
      onClick={async () => {
        if (!window.confirm(copy.deleteConfirm)) return;
        if (busyRef.current) return;
        const token = request.current;
        const key = scopeKey;
        const operation = ++busyToken.current;
        busyRef.current = true;
        setBusy(true);
        try {
          await voiceApi.delete();
          await load(key, token);
        } catch (e) {
          if (token === request.current && key === scopeKey) setError(signatureError(e, copy));
        } finally {
          if (token === request.current && key === scopeKey) setBusy(false);
          if (busyToken.current === operation) busyRef.current = false;
        }
      }}
    >
      {copy.delete}
    </button>
  );
  if (
    state.available &&
    state.org.signaturesEnabled &&
    state.consent?.version !== state.currentConsentVersion
  )
    return (
      <section className="company-form">
        <h2>{copy.consentTitle}</h2>
        <p>{copy.consentBody}</p>
        <label className="company-check-row">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setChecked(e.target.checked)}
          />{' '}
          {copy.consentCheckbox}
        </label>
        {error && <p role="alert">{error}</p>}
        <button
          className="company-primary"
          disabled={!checked || busy}
          onClick={async () => {
            if (busyRef.current) return;
            const token = request.current;
            const key = scopeKey;
            const operation = ++busyToken.current;
            busyRef.current = true;
            setBusy(true);
            try {
              await voiceApi.consent({
                version: state.currentConsentVersion,
                method: 'settings',
                locale,
              });
              await load(key, token);
            } catch (e) {
              if (token === request.current && key === scopeKey) setError(signatureError(e, copy));
            } finally {
              if (token === request.current && key === scopeKey) setBusy(false);
              if (busyToken.current === operation) busyRef.current = false;
            }
          }}
        >
          {copy.agree}
        </button>
        {state.voiceprint && deleteButton}
      </section>
    );
  return (
    <section className="company-form">
      <h2>{copy.title}</h2>
      {error && <p role="alert">{error}</p>}
      {!state.available && <p role="alert">{copy.unavailable}</p>}
      {!state.org.signaturesEnabled && <p role="status">{copy.orgOff}</p>}
      {!state.voiceprint && <p>{copy.whatItIs}</p>}
      {state.available &&
      state.org.signaturesEnabled &&
      state.voiceprint?.status === 'stale_model' ? (
        <>
          <p role="status">{copy.staleModel}</p>
          <EnrolmentRecorder key={scopeKey} onDone={reload} copy={copy} />
        </>
      ) : state.voiceprint?.status === 'stale_model' ? (
        <p role="status">{copy.staleModel}</p>
      ) : state.voiceprint ? (
        <p role="status">{copy.recognisedInMeetings}</p>
      ) : state.available && state.org.signaturesEnabled ? (
        <EnrolmentRecorder key={scopeKey} onDone={reload} copy={copy} />
      ) : null}
      {state.voiceprint?.status === 'stale_model' &&
        Number.isSafeInteger(suggestionCount ?? state.voiceprint.suggestionCount) &&
        (suggestionCount ?? state.voiceprint.suggestionCount)! >= 0 && (
          <p>
            {copy.usage.replace(
              '{count}',
              new Intl.NumberFormat(locale).format(
                suggestionCount ?? state.voiceprint.suggestionCount!,
              ),
            )}
          </p>
        )}
      {state.voiceprint?.status === 'stale_model' && (
        <button className="company-secondary" onClick={() => ws.navigate('meetings')}>
          {meetingCopy.useMyVoice}
        </button>
      )}
      {state.voiceprint?.sources?.length ? (
        <details>
          <summary>{copy.sources}</summary>
          <ul>
            {state.voiceprint.sources.map((source) => (
              <li key={source.id}>
                {source.kind === 'meeting'
                  ? copy.sourceMeeting
                      .replace('{title}', source.meetingTitle ?? '')
                      .replace('{date}', new Date(source.date).toLocaleDateString(locale))
                  : copy.sourceRecording.replace(
                      '{date}',
                      new Date(source.date).toLocaleDateString(locale),
                    )}
                <button
                  className="ws-citation"
                  disabled={busy}
                  onClick={async () => {
                    if (busyRef.current) return;
                    const token = request.current;
                    const key = scopeKey;
                    const operation = ++busyToken.current;
                    busyRef.current = true;
                    setBusy(true);
                    try {
                      await voiceApi.removeSource(source.id);
                      await load(key, token);
                    } catch (e) {
                      if (token === request.current && key === scopeKey)
                        setError(signatureError(e, copy));
                    } finally {
                      if (token === request.current && key === scopeKey) setBusy(false);
                      if (busyToken.current === operation) busyRef.current = false;
                    }
                  }}
                >
                  {copy.removeSource}
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {deleteButton}
    </section>
  );
}
